import './css/Modal.css';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AttachedJobsList from './questionnaires/AttachedJobsList';

interface Question {
  questionId?: string;
  text?: string;
  questionText?: string;
  type: string;
  required: boolean;
  options?: Array<{ label: string; value: string }>;
}

interface ModalProps {
  id: string;
  questionnaireId: string;
  questionnaireName: string;
  questions: Question[];
  contentType: string;
  // VI-725 Step E: modal is now React-controlled (was Bootstrap data-toggle/data-dismiss) so we
  // can drop Bootstrap JS. Parent (Table) owns the open/close state.
  isOpen: boolean;
  onClose: () => void;
  // VI-579 follow-up (Bug 3): forwarded to the embedded AttachedJobsList so the questionnaires
  // table can correct its cached count when the modal's fresh fetch disagrees with it.
  onAttachedJobsCountResolved?: (count: number) => void;
}

const Modal: React.FC<ModalProps> = ({
  id,
  questionnaireId,
  questionnaireName,
  questions,
  contentType,
  isOpen,
  onClose,
  onAttachedJobsCountResolved,
}) => {
  const navigate = useNavigate();

  // Close on Escape + lock body scroll while open (mirrors the old Bootstrap modal behavior).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('modal-open');
    };
  }, [isOpen, onClose]);

  const handleEdit = () => {
    onClose();
    navigate(`/v1/recruiter/questionnaire-builder/${id}`);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div
        className="modal fade show"
        id={`modal-${id}`}
        tabIndex={-1}
        role="dialog"
        aria-labelledby={`modal-${id}-label`}
        aria-modal="true"
        style={{ display: 'block' }}
        onClick={(e) => {
          // clicking the dimmed area outside the dialog closes the modal
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id={`modal-${id}-label`}>Preview</h5>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={onClose}
              >
                <FontAwesomeIcon
                  icon={faTimes}
                  style={{
                    fontSize: '1.2rem',
                    cursor: 'pointer',
                    padding: '0.2rem'
                  }}
                />
              </button>
            </div>
            <div className="modal-body">
              <div className="d-flex justify-content-between align-items-start mb-4 gap-2">
                <div className="text-align-left flex-grow-1" style={{ minWidth: 0 }}>
                  <h4 style={{ wordBreak: 'break-word' }}>{questionnaireName}</h4>
                  <p>Manage job posts using question set or link new job posts</p>
                  <div
                    className="d-inline-flex align-items-center border px-2 py-1 text-secondary"
                    style={{ borderRadius: '10px' }}
                  >
                    <img
                      src={contentType === 'Video' ? '/svgs/video.svg' : contentType === 'Audio' ? '/svgs/mic.svg' : ''}
                      alt={contentType}
                      width="16"
                      height="16"
                      className="text-secondary"
                    />
                    <span className="ml-2">{contentType}</span>
                  </div>
                </div>

                <button
                  className="btn btn-outline-primary flex-shrink-0"
                  onClick={handleEdit}
                  id="edit-questionnaire-button"
                >
                  <i className="fa fa-pen-to-square mr-2"></i>Edit Questionnaire
                </button>
              </div>

              <div className="questions-container text-align-left">
                {Array.isArray(questions) && questions.length > 0 ? (
                  questions.map((question, index) => (
                    <div key={question.questionId || `question-${index}`} className="question-item mb-4">
                      <h6 className="question-text">
                        {index + 1}. {question.questionText || question.text}
                      </h6>
                      {question.type !== 'text' && question.options && (
                        <ul className="list-unstyled pl-4">
                          {question.options.map((option, optIndex) => (
                            <li key={optIndex} className="option-item mb-2">
                              • {option.label}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-muted">No questions available</p>
                )}
              </div>

              <div className="mt-3 attached-jobs-wrapper--preview">
                <AttachedJobsList
                  questionnaireId={questionnaireId}
                  onCountResolved={onAttachedJobsCountResolved}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Modal;
